import React from "react";
import { Box, LinearProgress, Tooltip, Typography } from "@mui/material";
import type { AgentContextUsage } from "../../types/agentChat";
import { formatTokenCount } from "../../utils/formatTokenCount";

interface ContextWindowMeterProps {
  contextUsage?: AgentContextUsage | null;
  compact?: boolean;
}

function meterColor(percent: number): "primary" | "warning" | "error" {
  if (percent >= 90) return "error";
  if (percent >= 70) return "warning";
  return "primary";
}

const ContextWindowMeter: React.FC<ContextWindowMeterProps> = ({
  contextUsage,
  compact = false,
}) => {
  if (!contextUsage) {
    return null;
  }

  const { used_tokens, limit_tokens, usage_percent } = contextUsage;
  const color = meterColor(usage_percent);
  const label = `Context: ${formatTokenCount(used_tokens)} / ${formatTokenCount(limit_tokens)} (${usage_percent}%)`;
  const tooltip =
    "Input context used by the model on the last reply (peak across tool steps).";

  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          minWidth: compact ? 100 : 140,
          maxWidth: compact ? 140 : 180,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", lineHeight: 1.2, mb: 0.25 }}
          noWrap
        >
          {label}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, usage_percent)}
          color={color}
          sx={{ height: compact ? 4 : 6, borderRadius: 1 }}
        />
      </Box>
    </Tooltip>
  );
};

export default ContextWindowMeter;
